import { Badge, Card, Text, Group, Stack, ThemeIcon } from '@mantine/core';
import { IconArrowRight, IconExternalLink } from '@tabler/icons-react';
import type { Product } from '../products';
import { productHref, opensExternally } from '../products';
import { productIcon } from '../productIcons';
import { DEMO_MODE } from '../config';

export function ProductTile({ product }: { product: Product }) {
  const Icon = productIcon(product.id);
  const accent = product.color === 'spring' ? 'spring' : 'aquifer';
  const external = opensExternally(product, DEMO_MODE);
  const hasUnit = DEMO_MODE && product.unitUrl != null;

  return (
    <Card
      component="a"
      href={productHref(product, DEMO_MODE)}
      // Open the live demo unit in a new tab so the launcher stays open as
      // the hub; in-app placeholders navigate within the launcher.
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      shadow="sm"
      radius="md"
      p="lg"
      withBorder
      className="product-tile"
      style={{
        textDecoration: 'none',
        display: 'block',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group align="flex-start" gap="md" wrap="nowrap">
          <ThemeIcon
            variant="light"
            color={accent}
            size={44}
            radius="md"
            style={{ flexShrink: 0 }}
          >
            <Icon size={24} stroke={1.6} />
          </ThemeIcon>
          <Stack gap={4}>
            <Group gap={8} align="center">
              <Text fw={600} size="lg" c="aquifer.7">
                {product.label}
              </Text>
              {DEMO_MODE &&
                (hasUnit ? (
                  <Badge color="spring" variant="light" size="xs" radius="sm">
                    Live demo unit
                  </Badge>
                ) : (
                  <Badge color="mist" variant="light" size="xs" radius="sm">
                    Coming soon
                  </Badge>
                ))}
            </Group>
            <Text size="sm" c="dimmed" lh={1.5}>
              {product.description}
            </Text>
            <Text size="xs" c="spring.7" mt={2} ff="monospace">
              {product.url.replace('https://', '')}
            </Text>
          </Stack>
        </Group>
        <ThemeIcon
          variant="subtle"
          color={accent}
          size="md"
          radius="xl"
          mt={4}
          style={{ flexShrink: 0 }}
        >
          {external ? <IconExternalLink size={16} /> : <IconArrowRight size={18} />}
        </ThemeIcon>
      </Group>
    </Card>
  );
}
